import React, { useCallback, useState, useEffect, useRef, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import stylePropType from 'react-style-proptype';
import styled from 'styled-components';

import { DropdownMenu } from '../../molecules/DropdownMenu';
import { useOnClickOutside } from '../../../hooks';
import { DropdownContext } from './DropdownContext';

const DropdownContainer = styled.div`
  display: inline-flex;
  position: relative;
`;

const Dropdown = memo(
  ({
    className = null,
    menuStyle = null,
    onMouseEnter = null,
    toggleMenuOnMouseEnter = false,
    menuPlacement,
    menuOffset,
    toggleComponent,
    children,
    isNested = false
  }) => {
    const triggerRef = useRef(null);
    const containerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleEscapeKeyPress = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      window.addEventListener('keydown', handleEscapeKeyPress);
      return () => window.removeEventListener('keydown', handleEscapeKeyPress);
    }, [handleEscapeKeyPress]);

    const closeMenu = (evt) => {
      evt?.stopPropagation();
      setIsOpen(false);
    };

    const toggleMenu = useCallback(
      (evt) => {
        evt?.stopPropagation();
        evt?.preventDefault();
        setIsOpen(!isOpen);
      },
      [isOpen]
    );

    useOnClickOutside(containerRef, closeMenu);

    const contextValue = useMemo(() => ({ closeMenu }), []);

    // Create a memoized ref merging function and reference element

    return (
      <DropdownContainer
        className={className}
        ref={containerRef}
        onMouseEnter={onMouseEnter}
        onMouseLeave={toggleMenuOnMouseEnter ? closeMenu : null}
      >
        <DropdownContext.Provider value={contextValue}>
          {toggleComponent &&
            React.cloneElement(toggleComponent, {
              onClick: !toggleMenuOnMouseEnter
                ? (evt) => {
                    toggleComponent.props.onClick?.(evt);
                    toggleMenu(evt);
                  }
                : null,
              onMouseEnter: toggleMenuOnMouseEnter ? toggleMenu : null,
              isOpen,
              ref: triggerRef
            })}
          <DropdownMenu
            referenceElement={triggerRef.current}
            isOpen={isOpen}
            style={menuStyle}
            menuPlacement={menuPlacement}
            menuOffset={menuOffset}
            usePortal={isNested}
          >
            {children(closeMenu)}
          </DropdownMenu>
        </DropdownContext.Provider>
      </DropdownContainer>
    );
  }
);

Dropdown.propTypes = {
  children: PropTypes.func.isRequired,
  className: PropTypes.string,
  menuStyle: stylePropType,
  menuPlacement: PropTypes.string,
  menuOffset: PropTypes.number,
  toggleComponent: PropTypes.node.isRequired,
  onMouseEnter: PropTypes.func,
  toggleMenuOnMouseEnter: PropTypes.bool,
  isNested: PropTypes.bool
};

export { Dropdown, DropdownContext };
